import workspace from "../../workspace";
import {findLowestRightmostPoint} from "../../../../../helpers/coordinatesHelper";
import {canShapeBeAutoShifted, getLineDirection} from "../../../../../helpers/shapeHelper";
import sqftShapeManager from "../../../store/sqftShapeManager";
import {getPointDistance} from "../../../../../helpers/sqftHelper";
import {isHorizontalDirection, isVerticalDirection} from "../../../helpers/lineHelper";
import workspaceControllers from "../../../store/workspaceControllers";
import sqftShapeIntersectionsManager from "../../../store/sqftShapeIntersectionsManager";
import {
  canCabinetBoxesShareRun,
  getCabinetBoxPartId,
  isTallCabinetBox,
} from "../../../../../helpers/cabinetBoxCompatibilityHelper";

export default class ShapeAutoShuffle {
  __shape;
  __dragStartState = null;

  constructor(shape) {
    this.__shape = shape;
  }

  captureDragStartState = () => {
    // Drag start state is the baseline for same-run reorders. During 3D commit
    // snap metadata is cleared before placement, so current shape state is no
    // longer enough to know which cabinets were originally after the dragged one.
    this.__dragStartState = {
      snappedShapeGroup: this.__shape.shapeAbsolute.getSnappedShapeGroup(),
      bounds: sqftShapeManager.getShapeBoundsOnWorkspace(this.__shape),
    };
  }

  clearDragStartState = () => {
    this.__dragStartState = null;
  }

  handlePlaceShapeOnTheLine = () => {
    // Auto Shuffle
    const hoveredLines = workspace.getHoveredLines([this.__shape.id]);

    if (hoveredLines.length > 0) {
      // Get relevant shapes and lines
      const hoveredLine = this.getHoveredLine(hoveredLines);
      const hoveredShape = hoveredLine.getParent();

      if (this.canShapeShuffleWithHoveredShape(this.__shape, hoveredShape)) {
        const newPosition = this.calculateNewPosition(hoveredLine, hoveredShape);

        if (newPosition) {
          const sameGroupReorderContext = this.getSameGroupReorderContext(hoveredShape, hoveredLine);
          const shapesToShift = this.getShapesToShift(hoveredShape, newPosition, sameGroupReorderContext, hoveredLine);

          if (shapesToShift.length > 0) {
            this.performFullAutoShuffle(newPosition, hoveredLine, hoveredShape, sameGroupReorderContext, shapesToShift);
          } else {
            this.alignShapeToExistingShapes(newPosition, hoveredLine, hoveredShape);
          }

          this.__shape.resetState();
          sqftShapeIntersectionsManager.updateShapesIntersections();
          workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
          workspace.getHoveredLines(); // update hovered lines
          return true;
        }
      }
    }
  }

  /**
   * Check if inserting at this line would require moving any compatible shape.
   * Used by 3D placement preview to decide whether the target edge is INSERT
   * (red preview, shift neighbours) or SNAP (green preview, attach only).
   * @param {Object} hoveredShape - The shape being hovered
   * @param {Object} position - The position to check
   * @param {Object} hoveredLine - The line being hovered
   * @returns {boolean} - True if a shape needs to be shifted
   */
  checkForAdjacentShape(hoveredShape, position, hoveredLine) {
    const sameGroupReorderContext = this.getSameGroupReorderContext(hoveredShape, hoveredLine);
    return this.getShapesToShift(hoveredShape, position, sameGroupReorderContext, hoveredLine).length > 0;
  }

  /**
   * Perform the original auto-shuffle behavior
   */
  performFullAutoShuffle(newPosition, hoveredLine, hoveredShape, sameGroupReorderContext = undefined, shapesToShift = null) {
    const resolvedSameGroupReorderContext = sameGroupReorderContext === undefined
      ? this.getSameGroupReorderContext(hoveredShape, hoveredLine)
      : sameGroupReorderContext;

    // Move shape to new positions
    this.moveShapeToPositionAndRotate(newPosition, hoveredLine, {isInsertion: true});

    // Set same snapped shape group before get shapes to shift
    workspaceControllers.snappedShapeGroups.setSameSnappedShapesGroup(this.__shape, hoveredShape);

    // Get shapes to shift
    const resolvedShapesToShift = shapesToShift
      || this.getShapesToShift(hoveredShape, newPosition, resolvedSameGroupReorderContext, hoveredLine);

    // Calculate new positions
    const [xDiff, yDiff] = this.calculatePositionDiffForOtherShapes(hoveredLine);
    this.moveShapesToPositions(resolvedShapesToShift, xDiff, yDiff, hoveredLine, resolvedSameGroupReorderContext);
  }

  /**
   * Just align the current shape to existing shapes without shifting others
   */
  alignShapeToExistingShapes(newPosition, hoveredLine, hoveredShape) {
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    // Get the measurement points of the hovered shape
    const hoveredShapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(hoveredShape);
    if (!hoveredShapeBounds) {
      return;
    }

    // Calculate aligned position based on line direction
    let alignedPosition = {...newPosition};

    // Get current shape dimensions
    const currentShapeWidth = this.__shape.getWidth();
    const currentShapeDepth = this.__shape.getDepth();

    if (isVerticalDirection(hoveredLineDirection)) {
      // For vertical line - position shape to the left of the hovered shape
      const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();

      // Check which edge of the shape we're on
      if (hoveredShapeBounds.left === hoveredLinePointFromPosOnWorkspace.x) {
        // We're on the left edge - place our shape to the left
        alignedPosition.x = hoveredShapeBounds.left - currentShapeWidth;
      } else if (hoveredShapeBounds.right === hoveredLinePointFromPosOnWorkspace.x) {
        // We're on the right edge - place our shape to the right
        alignedPosition.x = hoveredShapeBounds.right;
      }

      // Keep Y position aligned with the hovered shape
      alignedPosition.y = hoveredShapeBounds.top;

    } else if (isHorizontalDirection(hoveredLineDirection)) {
      // For horizontal line - position shape above or below the hovered shape
      const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();

      // Check which edge of the shape we're on
      if (hoveredShapeBounds.top === hoveredLinePointFromPosOnWorkspace.y) {
        // We're on the top edge - place our shape above
        alignedPosition.y = hoveredShapeBounds.top - currentShapeDepth;
      } else if (hoveredShapeBounds.bottom === hoveredLinePointFromPosOnWorkspace.y) {
        // We're on the bottom edge - place our shape below
        alignedPosition.y = hoveredShapeBounds.bottom;
      }

      // Keep X position aligned with the hovered shape
      alignedPosition.x = hoveredShapeBounds.left;
    }

    // Move and rotate the shape first
    this.moveShapeToPositionAndRotate(alignedPosition, hoveredLine);

    // Set same snapped shape group
    workspaceControllers.snappedShapeGroups.setSameSnappedShapesGroup(this.__shape, hoveredShape);
  }

  /**
   * Will return line for the lowest and rightmost shape
   * @param hoveredLines
   */
  getHoveredLine = (hoveredLines) => {
    const hoveredLinesShapes = hoveredLines.map(line => line.getParent());
    const lowestRightmostPoint = findLowestRightmostPoint(hoveredLinesShapes.map(shape => shape.getPosition()));
    const shapeToShift = hoveredLinesShapes.find(shape => {
      return shape.getPosition() === lowestRightmostPoint
        && shape.id !== this.__shape.id // skip current moving shape
    });

    return hoveredLines.find(line => line.getParent().id === shapeToShift.id);
  }

  /**
   * Will return all shapes that are lower or on the same line then point
   * @param hoveredShape
   * @param point
   * @param sameGroupReorderContext
   */
  getShapesToShift = (hoveredShape, point, sameGroupReorderContext = null, hoveredLine = null) => {
    const compatibleShapes = this.getCompatibleShapesForShuffle(hoveredShape);
    if (sameGroupReorderContext) {
      // Same-run reorders should use the vacated slot in that snapped group;
      // cross-lane tall collision rules would incorrectly push unrelated runs.
      const sameGroupCompatibleShapes = this.getSameGroupReorderCompatibleShapes(compatibleShapes, hoveredLine);
      const sameGroupShapesToShift = this.findSameGroupReorderShapesToShift(
        sameGroupCompatibleShapes,
        point,
        sameGroupReorderContext
      );
      return this.expandShapesToShiftForTallCabinetCascade(sameGroupShapesToShift, hoveredLine);
    }

    const shapesToShift = this.findShapesToShiftForLine(compatibleShapes, point, hoveredLine);

    return this.expandShapesToShiftForTallCabinetCascade(shapesToShift, hoveredLine);
  }

  getCompatibleShapesForShuffle(hoveredShape) {
    const snappedShapeGroup = hoveredShape.shapeAbsolute.getSnappedShapeGroup();
    let shapesToCheck = [hoveredShape];

    if (snappedShapeGroup !== null && snappedShapeGroup !== undefined) {
      shapesToCheck = workspaceControllers.snappedShapeGroups.getSnappedShapes(snappedShapeGroup) || [];
    }

    if (canCabinetBoxesShareRun(this.__shape, hoveredShape)) {
      shapesToCheck = [
        ...shapesToCheck,
        ...this.getAdditionalCabinetRunShapes(hoveredShape),
      ];
    }

    return this.getUniqueShapes(shapesToCheck).filter(shape =>
      shape.id !== this.__shape.id
      && !shape.isHidden
      && typeof shape.getLines === 'function'
      && this.isShapeCompatibleWithHoveredShape(shape, this.__shape)
      && this.isShapeCompatibleWithHoveredShape(shape, hoveredShape)
    );
  }

  // Tall cabinets can live in upper/lower/tall feature groups, but physically
  // occupy both cabinet lanes, so compatible run checks must include them
  // outside the hovered snapped group.
  getAdditionalCabinetRunShapes(hoveredShape) {
    const movingShapePartId = getCabinetBoxPartId(this.__shape);
    const hoveredShapePartId = getCabinetBoxPartId(hoveredShape);

    if (!movingShapePartId || !hoveredShapePartId) {
      return [];
    }

    const shouldIncludeMovingLayer = isTallCabinetBox(hoveredShape);

    return (workspace.getShapes(true) || []).filter(shape => {
      const shapePartId = getCabinetBoxPartId(shape);
      if (!shapePartId) {
        return false;
      }

      return isTallCabinetBox(shape)
        || (shouldIncludeMovingLayer && shapePartId === movingShapePartId);
    });
  }

  getUniqueShapes(shapes) {
    const shapeIds = new Set();

    return shapes.filter(shape => {
      if (!shape || shapeIds.has(shape.id)) {
        return false;
      }

      shapeIds.add(shape.id);
      return true;
    });
  }

  isShapeCompatibleWithHoveredShape(shape, hoveredShape) {
    if (canCabinetBoxesShareRun(shape, hoveredShape)) {
      return true;
    }

    return hoveredShape.partId === shape.partId
      || hoveredShape.partId === shape.parentPartId
      || (hoveredShape.parentPartId && hoveredShape.parentPartId === shape.partId);
  }

  canShapeShuffleWithHoveredShape(shape, hoveredShape) {
    return canShapeBeAutoShifted(shape, hoveredShape)
      || canCabinetBoxesShareRun(shape, hoveredShape);
  }

  // Once a tall cabinet shifts, any directly attached cabinet to its right/bottom
  // must move with it to keep the run contiguous. Separate cabinets are excluded
  // by adjacency and lane-overlap checks.
  expandShapesToShiftForTallCabinetCascade(shapesToShift, hoveredLine) {
    if (!hoveredLine || shapesToShift.length === 0) {
      return shapesToShift;
    }

    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const resultShapes = this.getUniqueShapes(shapesToShift);
    const resultShapeIds = new Set(resultShapes.map(shape => shape.id));
    const checkedShapeIds = new Set();
    const shapesQueue = [...resultShapes];

    while (shapesQueue.length > 0) {
      const sourceShape = shapesQueue.shift();
      if (!sourceShape || checkedShapeIds.has(sourceShape.id)) {
        continue;
      }

      checkedShapeIds.add(sourceShape.id);

      this.getCabinetRunCascadeCandidates(sourceShape).forEach(candidateShape => {
        const shouldCascade = this.shouldCascadeShiftFromShape(candidateShape, sourceShape, hoveredLineDirection);

        if (
          resultShapeIds.has(candidateShape.id)
          || !shouldCascade
        ) {
          return;
        }

        resultShapeIds.add(candidateShape.id);
        resultShapes.push(candidateShape);
        shapesQueue.push(candidateShape);
      });
    }

    return resultShapes;
  }

  getShapeSnappedGroup(shape) {
    return shape && shape.shapeAbsolute
      ? shape.shapeAbsolute.getSnappedShapeGroup()
      : null;
  }

  areShapesInSameSnappedGroup(firstShape, secondShape) {
    const firstShapeGroup = this.getShapeSnappedGroup(firstShape);
    const secondShapeGroup = this.getShapeSnappedGroup(secondShape);

    return firstShapeGroup !== null
      && firstShapeGroup !== undefined
      && firstShapeGroup === secondShapeGroup;
  }

  getSameGroupReorderCompatibleShapes(shapes, hoveredLine) {
    if (!hoveredLine) {
      return shapes;
    }

    const hoveredShape = hoveredLine.getParent();

    return shapes.filter(shape =>
      shape.id === hoveredShape.id
      || this.areShapesInSameSnappedGroup(shape, hoveredShape)
    );
  }

  // The cascade considers all workspace cabinets because tall cabinets may
  // overlap a top or bottom run without sharing its snapped group.
  getCabinetRunCascadeCandidates(sourceShape) {
    return (workspace.getShapes(true) || []).filter(shape =>
      shape.id !== this.__shape.id
      && !shape.isHidden
      && typeof shape.getLines === 'function'
      && canCabinetBoxesShareRun(shape, sourceShape)
    );
  }

  shouldCascadeShiftFromShape(shape, sourceShape, hoveredLineDirection) {
    const shapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(shape);
    const sourceShapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(sourceShape);
    const threshold = 0.5;

    if (!shapeBounds || !sourceShapeBounds) {
      return false;
    }

    if (isVerticalDirection(hoveredLineDirection)) {
      return shapeBounds.left >= sourceShapeBounds.left - threshold
        && shapeBounds.left <= sourceShapeBounds.right + threshold
        && this.rangesOverlap(shapeBounds.top, shapeBounds.bottom, sourceShapeBounds.top, sourceShapeBounds.bottom);
    }

    if (isHorizontalDirection(hoveredLineDirection)) {
      return shapeBounds.top >= sourceShapeBounds.top - threshold
        && shapeBounds.top <= sourceShapeBounds.bottom + threshold
        && this.rangesOverlap(shapeBounds.left, shapeBounds.right, sourceShapeBounds.left, sourceShapeBounds.right);
    }

    return false;
  }

  // Same-run reorders can reuse the vacated cabinet slot instead of pushing
  // every later cabinet in the snapped group. This only applies when the moving
  // cabinet started in the same snapped group and on the same run axis.
  getSameGroupReorderContext(hoveredShape, hoveredLine) {
    const movingShapeGroup = this.__dragStartState
      ? this.__dragStartState.snappedShapeGroup
      : this.__shape.shapeAbsolute.getSnappedShapeGroup();
    const hoveredShapeGroup = hoveredShape.shapeAbsolute.getSnappedShapeGroup();

    if (
      movingShapeGroup === null
      || movingShapeGroup === undefined
      || movingShapeGroup !== hoveredShapeGroup
    ) {
      return null;
    }

    const movingShapeOriginalBounds = this.__dragStartState
      ? this.__dragStartState.bounds
      : sqftShapeManager.getShapeBoundsOnWorkspace(this.__shape);
    const hoveredShapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(hoveredShape);
    if (!movingShapeOriginalBounds || !hoveredShapeBounds) {
      return null;
    }
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const isVerticalInsertionEdge = isVerticalDirection(hoveredLineDirection);
    const isHorizontalInsertionEdge = isHorizontalDirection(hoveredLineDirection);
    const isSameHorizontalRun = isVerticalInsertionEdge
      && this.rangesOverlapByRatio(
        movingShapeOriginalBounds.top,
        movingShapeOriginalBounds.bottom,
        hoveredShapeBounds.top,
        hoveredShapeBounds.bottom
      );
    const isSameVerticalRun = isHorizontalInsertionEdge
      && this.rangesOverlapByRatio(
        movingShapeOriginalBounds.left,
        movingShapeOriginalBounds.right,
        hoveredShapeBounds.left,
        hoveredShapeBounds.right
      );

    if (!isSameHorizontalRun && !isSameVerticalRun) {
      return null;
    }

    return {
      isVerticalInsertionEdge,
      isHorizontalInsertionEdge,
      movingShapeOriginalBounds,
    };
  }

  rangesOverlap(firstStart, firstEnd, secondStart, secondEnd) {
    return Math.min(firstEnd, secondEnd) > Math.max(firstStart, secondStart);
  }

  rangesOverlapByRatio(firstStart, firstEnd, secondStart, secondEnd) {
    const overlap = Math.min(firstEnd, secondEnd) - Math.max(firstStart, secondStart);
    if (overlap <= 0) {
      return false;
    }

    const firstSize = Math.abs(firstEnd - firstStart);
    const secondSize = Math.abs(secondEnd - secondStart);
    const overlapBase = Math.min(firstSize, secondSize);

    return overlapBase > 0 && overlap / overlapBase >= 0.5;
  }

  findSameGroupReorderShapesToShift(shapes, insertPoint, sameGroupReorderContext) {
    const {movingShapeOriginalBounds} = sameGroupReorderContext;
    const threshold = 0.5;

    // Dropping back onto the dragged cabinet's original start edge is not a
    // reorder. Treat it as no-op for neighbours so the run does not open a gap.
    if (
      sameGroupReorderContext.isVerticalInsertionEdge
      && Math.abs(insertPoint.x - movingShapeOriginalBounds.left) < threshold
    ) {
      return [];
    }

    if (
      sameGroupReorderContext.isHorizontalInsertionEdge
      && Math.abs(insertPoint.y - movingShapeOriginalBounds.top) < threshold
    ) {
      return [];
    }

    return shapes.filter(shape => {
      const shapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(shape);
      if (!shapeBounds) {
        return false;
      }

      const shapeLeftX = shapeBounds.left;
      const shapeTopY = shapeBounds.top;

      if (sameGroupReorderContext.isVerticalInsertionEdge) {
        // Moving right/equal behaves like a normal insert from this point onward.
        // Moving left shifts only cabinets between the insert point and the
        // dragged cabinet's original slot; cabinets after that slot stay put.
        if (insertPoint.x >= movingShapeOriginalBounds.left - threshold) {
          return shapeLeftX >= insertPoint.x - threshold;
        }

        return shapeLeftX >= insertPoint.x - threshold
          && shapeLeftX < movingShapeOriginalBounds.left - threshold;
      }

      if (sameGroupReorderContext.isHorizontalInsertionEdge) {
        // Same rule for vertical runs: only the range between destination and
        // original slot shifts when moving upward in the same snapped group.
        if (insertPoint.y >= movingShapeOriginalBounds.top - threshold) {
          return shapeTopY >= insertPoint.y - threshold;
        }

        return shapeTopY >= insertPoint.y - threshold
          && shapeTopY < movingShapeOriginalBounds.top - threshold;
      }

      return false;
    });
  }

  /**
   * Calculate new position based on hovered line
   */
  calculateNewPosition(hoveredLine, shapeToShift) {
    const shapeToShiftMeasurementPoints = sqftShapeManager.getShapeMeasurementPointsOnWorkspace(shapeToShift);
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    let newShapePosition = null;
    const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();

    if (isVerticalDirection(hoveredLineDirection)) {
      // Check whether it is shape start or end
      if (shapeToShiftMeasurementPoints[1].x === hoveredLinePointFromPosOnWorkspace.x) {
        newShapePosition = shapeToShiftMeasurementPoints[1];
        // Shift shape only if it is first line
      } else if (shapeToShiftMeasurementPoints[0].x === hoveredLinePointFromPosOnWorkspace.x) {
        newShapePosition = shapeToShiftMeasurementPoints[0];
      }
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      // Check whether it is shape start or end
      if (shapeToShiftMeasurementPoints[0].y === hoveredLinePointFromPosOnWorkspace.y) {
        newShapePosition = shapeToShiftMeasurementPoints[0];
        // Shift shape only if it is first line
      } else if (shapeToShiftMeasurementPoints[3].y === hoveredLinePointFromPosOnWorkspace.y) {
        newShapePosition = shapeToShiftMeasurementPoints[3];
      }
    }

    return newShapePosition
  }

  /**
   * Calculate position difference for other shapes
   */
  calculatePositionDiffForOtherShapes(hoveredLine) {
    let xDiff = 0;
    let yDiff = 0;
    const thisShapeMeasurementPoints = sqftShapeManager.getShapeMeasurementPointsOnWorkspace(this.__shape);
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    if (isVerticalDirection(hoveredLineDirection)) {
      xDiff = getPointDistance(thisShapeMeasurementPoints[0], thisShapeMeasurementPoints[1]);
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      yDiff = getPointDistance(thisShapeMeasurementPoints[3], thisShapeMeasurementPoints[0]);
    }

    return [xDiff, yDiff]
  }

  /**
   * Move shapes to new positions
   */
  moveShapesToPositions(shapesToShift, xDiff, yDiff, hoveredLine = null, sameGroupReorderContext = null) {
    const shapeShiftOverrides = sameGroupReorderContext
      ? {}
      : this.getTallCabinetOverlapShiftOverrides(shapesToShift, xDiff, yDiff, hoveredLine);

    shapesToShift.forEach(shape => {
      const shapeCurrentPos = shape.getPosition();
      const shiftOverride = shapeShiftOverrides[shape.id];
      const shapeNewPos = {
        x: shapeCurrentPos.x + (shiftOverride ? shiftOverride.xDiff : xDiff),
        y: shapeCurrentPos.y + (shiftOverride ? shiftOverride.yDiff : yDiff),
      };
      shape.setPosition(shapeNewPos);
      shape.shapeAbsolute.setAbsolutePos(shapeNewPos);
    })
  }

  getTallCabinetOverlapShiftOverrides(shapesToShift, defaultXDiff, defaultYDiff, hoveredLine) {
    if (!hoveredLine || !shapesToShift.some(shape => isTallCabinetBox(shape))) {
      return {};
    }

    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const movedShapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(this.__shape);
    if (!movedShapeBounds) {
      return {};
    }

    const overrides = {};
    const tallCabinetShapes = shapesToShift.filter(shape => isTallCabinetBox(shape));
    tallCabinetShapes.forEach(tallCabinet => {
      // Only the first tall in a connected chain may calculate a partial shift.
      // Downstream tall cabinets inherit that shift through the cascade so they
      // do not collapse onto the first cabinet.
      if (this.hasUpstreamShiftShape(shapesToShift, tallCabinet, hoveredLineDirection)) {
        return;
      }

      const tallCabinetBounds = sqftShapeManager.getShapeBoundsOnWorkspace(tallCabinet);
      if (!tallCabinetBounds) {
        return;
      }

      let xDiff = defaultXDiff;
      let yDiff = defaultYDiff;
      let hasPartialOverlapShift = false;

      if (isVerticalDirection(hoveredLineDirection)) {
        const overlapShift = movedShapeBounds.right - tallCabinetBounds.left;
        if (overlapShift > 0 && overlapShift < Math.abs(defaultXDiff)) {
          xDiff = overlapShift;
          hasPartialOverlapShift = true;
        }
      } else if (isHorizontalDirection(hoveredLineDirection)) {
        const overlapShift = movedShapeBounds.bottom - tallCabinetBounds.top;
        if (overlapShift > 0 && overlapShift < Math.abs(defaultYDiff)) {
          yDiff = overlapShift;
          hasPartialOverlapShift = true;
        }
      }

      if (!hasPartialOverlapShift) {
        return;
      }

      this.getTallCabinetCascadeShapeIds(shapesToShift, tallCabinet, hoveredLineDirection).forEach(shapeId => {
        if (!overrides[shapeId]) {
          overrides[shapeId] = {xDiff, yDiff};
        }
      });
    });

    return overrides;
  }

  // A tall cabinet with another shifted cabinet before it is downstream in the
  // same run and must not calculate an independent overlap distance.
  hasUpstreamShiftShape(shapesToShift, targetShape, hoveredLineDirection) {
    return shapesToShift.some(shape =>
      shape.id !== targetShape.id
      && this.shouldCascadeShiftFromShape(targetShape, shape, hoveredLineDirection)
    );
  }

  getTallCabinetCascadeShapeIds(shapesToShift, tallCabinet, hoveredLineDirection) {
    return shapesToShift
      .filter(shape =>
        shape.id === tallCabinet.id
        || this.shouldCascadeShiftFromShape(shape, tallCabinet, hoveredLineDirection)
      )
      .map(shape => shape.id);
  }

  /**
   * Moves and rotates the shape based on the line's orientation and the current rotation angle
   */
  moveShapeToPositionAndRotate(newPosition, hoveredLine, options = {}) {
    const hoveredShape = hoveredLine.getParent();
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const isHorizontal = isHorizontalDirection(hoveredLineDirection);
    const isVertical = isVerticalDirection(hoveredLineDirection);

    // 1) Compensate local offset between shape position and its first point after resize
    let targetPos = { ...newPosition };

    if (typeof this.__shape.getOriginalPointsCoords === "function"
      && typeof this.__shape.getPointsCoords === "function") {

      const originalPoints = this.__shape.getOriginalPointsCoords() || [];
      const currentPoints = this.__shape.getPointsCoords() || [];

      if (originalPoints.length > 0 && currentPoints.length > 0) {
        const originalFirst = originalPoints[0]; // before resize
        const currentFirst = currentPoints[0];   // after resize

        // How much did the first point move inside local coords
        const dxLocal = currentFirst.x - originalFirst.x;
        const dyLocal = currentFirst.y - originalFirst.y;

        // We want FIRST POINT to end up at newPosition,
        // so we move shape position in the opposite direction
        targetPos = {
          x: newPosition.x - dxLocal,
          y: newPosition.y - dyLocal,
        };
      }
    }

    // 2) Apply compensated position
    this.__shape.setPosition(targetPos);
    this.__shape.shapeAbsolute.setAbsolutePos(targetPos);

    // 3) Rotation logic (как у тебя было)
    if (hoveredShape.rotatedDegrees === 270 && isHorizontal) {
      const desiredAngle = 270;
      const normalizedCurrentAngle = ((this.__shape.rotatedDegrees % 360) + 360) % 360;
      const angleDifference = desiredAngle - normalizedCurrentAngle;
      this.__shape.rotate(angleDifference, this.__shape.Direction.LEFT, false);
    } else if (this.__shape.rotatedDegrees === 0 && isHorizontal) {
      this.__shape.rotate(90, this.__shape.Direction.LEFT, false);
    } else if (this.__shape.rotatedDegrees === 90 && isVertical) {
      this.__shape.rotate(-90, this.__shape.Direction.LEFT, false);
    }

    // 4) FINAL ALIGNMENT after rotation – stick bbox to hovered line
    const hoveredShapeMP = sqftShapeManager.getShapeMeasurementPointsOnWorkspace(hoveredShape);
    const linePos = hoveredLine.from.getPointPosOnWorkspace();
    const thisShapeMP = sqftShapeManager.getShapeMeasurementPointsOnWorkspace(this.__shape);

    let dx = 0;
    let dy = 0;
    const threshold = 0.5;

    if (isVertical) {
      const targetX = linePos.x;
      const hoveredLeftX = hoveredShapeMP[0].x;
      const hoveredRightX = hoveredShapeMP[1].x;
      const thisLeftX = thisShapeMP[0].x;
      const thisRightX = thisShapeMP[1].x;

      if (options.isInsertion) {
        dx = targetX - thisLeftX;
      } else if (Math.abs(hoveredLeftX - targetX) < threshold) {
        dx = targetX - thisRightX;
      } else if (Math.abs(hoveredRightX - targetX) < threshold) {
        dx = targetX - thisLeftX;
      }

      dy = hoveredShapeMP[0].y - thisShapeMP[0].y;

    } else if (isHorizontal) {
      const targetY = linePos.y;
      const hoveredTopY = hoveredShapeMP[0].y;
      const hoveredBottomY = hoveredShapeMP[3].y;
      const thisTopY = thisShapeMP[0].y;
      const thisBottomY = thisShapeMP[3].y;

      // If we are on TOP edge – our BOTTOM edge should touch it
      if (options.isInsertion) {
        dy = targetY - thisTopY;
      } else if (Math.abs(hoveredTopY - targetY) < threshold) {
        dy = targetY - thisBottomY;
      }
      // If we are on BOTTOM edge – our TOP edge should touch it
      else if (Math.abs(hoveredBottomY - targetY) < threshold) {
        dy = targetY - thisTopY;
      }

      // align the left edge along the X
      const targetLeftX = hoveredShapeMP[0].x;
      const thisLeftX = thisShapeMP[0].x;
      dx = targetLeftX - thisLeftX;
    }

    if (dx !== 0 || dy !== 0) {
      const currentPos = this.__shape.getPosition();
      const finalPos = {
        x: currentPos.x + dx,
        y: currentPos.y + dy
      };

      this.__shape.setPosition(finalPos);
      this.__shape.shapeAbsolute.setAbsolutePos(finalPos);
    }
  }


  /**
   * Function for finding all shapes that are to the right and below a point
   * @param {Array} shapes
   * @param referencePosition
   * @returns {Array}
   */
  findShapesRightAndBelow(shapes, referencePosition) {
    const resultShapes = [];

    shapes.forEach(shape => {
      const shapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(shape);
      if (!shapeBounds) {
        return;
      }

      const shapeTopLeftPoint = {
        x: shapeBounds.left,
        y: shapeBounds.top,
      };

      if (shapeTopLeftPoint.x >= referencePosition.x && shapeTopLeftPoint.y >= referencePosition.y) {
        resultShapes.push(shape);
      }
    });

    return resultShapes;
  }

  findShapesToShiftForLine(shapes, referencePosition, hoveredLine) {
    if (!hoveredLine) {
      return this.findShapesRightAndBelow(shapes, referencePosition);
    }

    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const hoveredLineFrom = hoveredLine.from.getPointPosOnWorkspace();
    const hoveredLineTo = hoveredLine.to.getPointPosOnWorkspace();
    const insertionBounds = this.getInsertionBounds(referencePosition, hoveredLine);
    if (!insertionBounds) {
      return [];
    }

    return shapes.filter(shape => {
      const shapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(shape);
      if (!shapeBounds) {
        return false;
      }

      // External inserts only shift cabinets that physically overlap the new
      // cabinet footprint. Adjacent cabinets after a gap stay put; connected
      // neighbours are picked up later by the cascade.
      if (isVerticalDirection(hoveredLineDirection)) {
        const lineTop = Math.min(hoveredLineFrom.y, hoveredLineTo.y);
        const lineBottom = Math.max(hoveredLineFrom.y, hoveredLineTo.y);
        const isOnSameRun = this.rangesOverlap(shapeBounds.top, shapeBounds.bottom, lineTop, lineBottom);

        return isOnSameRun && this.doesShapeOverlapInsertion(shapeBounds, insertionBounds);
      }

      if (isHorizontalDirection(hoveredLineDirection)) {
        const lineLeft = Math.min(hoveredLineFrom.x, hoveredLineTo.x);
        const lineRight = Math.max(hoveredLineFrom.x, hoveredLineTo.x);
        const isOnSameRun = this.rangesOverlap(shapeBounds.left, shapeBounds.right, lineLeft, lineRight);

        return isOnSameRun && this.doesShapeOverlapInsertion(shapeBounds, insertionBounds);
      }

      return false;
    });
  }

  doesShapeOverlapInsertion(shapeBounds, insertionBounds) {
    const threshold = 0.5;

    return shapeBounds.right > insertionBounds.left + threshold
      && shapeBounds.left < insertionBounds.right - threshold
      && shapeBounds.bottom > insertionBounds.top + threshold
      && shapeBounds.top < insertionBounds.bottom - threshold;
  }

  getInsertionBounds(referencePosition, hoveredLine) {
    const currentShapeBounds = sqftShapeManager.getShapeBoundsOnWorkspace(this.__shape);
    if (!currentShapeBounds || !hoveredLine) {
      return null;
    }

    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const hoveredLineFrom = hoveredLine.from.getPointPosOnWorkspace();
    const hoveredLineTo = hoveredLine.to.getPointPosOnWorkspace();
    const currentShapeWidth = currentShapeBounds.right - currentShapeBounds.left;
    const currentShapeHeight = currentShapeBounds.bottom - currentShapeBounds.top;

    if (isVerticalDirection(hoveredLineDirection)) {
      return {
        left: referencePosition.x,
        right: referencePosition.x + currentShapeWidth,
        top: Math.min(hoveredLineFrom.y, hoveredLineTo.y),
        bottom: Math.max(hoveredLineFrom.y, hoveredLineTo.y),
      };
    }

    if (isHorizontalDirection(hoveredLineDirection)) {
      return {
        left: Math.min(hoveredLineFrom.x, hoveredLineTo.x),
        right: Math.max(hoveredLineFrom.x, hoveredLineTo.x),
        top: referencePosition.y,
        bottom: referencePosition.y + currentShapeHeight,
      };
    }

    return null;
  }

}
